/**
 * EnhancedJsonEditorEditor
 * Configuration for enhanced-json-editor control
 * Uses LIST/ARRAY PATTERN for schema validation configuration
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface JsonSchema {
  type: string;
  properties?: Record<string, unknown>;
  required?: string[];
  format?: string;
}

interface EnhancedJsonEditorConfig {
  schema?: JsonSchema;
  validate?: boolean;
  indentation?: number;
  lineNumbers?: boolean;
  theme?: string;
  readOnly?: boolean;
  minLines?: number;
  maxLines?: number;
}

function renderEnhancedJsonEditorEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as EnhancedJsonEditorConfig;

  const schema = config.schema || {};
  const validate = config.validate ?? true;
  const indentation = config.indentation ?? 2;
  const lineNumbers = config.lineNumbers ?? true;
  const theme = config.theme || 'default';
  const readOnly = config.readOnly ?? false;
  const minLines = config.minLines ?? 5;
  const maxLines = config.maxLines ?? 20;

  const handleSchemaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      try {
        const parsed = JSON.parse(e.target.value);
        onConfigChange('schema', parsed);
      } catch {
        // Invalid JSON, ignore
      }
    },
    [onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Enhanced JSON Editor Configuration</h4>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={validate}
            onChange={(e) => onConfigChange('validate', e.target.checked)}
          />
          Enable Schema Validation
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={lineNumbers}
            onChange={(e) => onConfigChange('lineNumbers', e.target.checked)}
          />
          Show Line Numbers
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={readOnly}
            onChange={(e) => onConfigChange('readOnly', e.target.checked)}
          />
          Read-Only Mode
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="indentation-select" className="af-properties-tab__label">
          Indentation
        </label>
        <select
          id="indentation-select"
          value={indentation}
          onChange={(e) => onConfigChange('indentation', Number(e.target.value))}
          className="af-properties-tab__select"
        >
          <option value="2">2 spaces</option>
          <option value="4">4 spaces</option>
          <option value="8">8 spaces</option>
          <option value="1">Tab</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="theme-select" className="af-properties-tab__label">
          Theme
        </label>
        <select
          id="theme-select"
          value={theme}
          onChange={(e) => onConfigChange('theme', e.target.value)}
          className="af-properties-tab__select"
        >
          <option value="default">Default</option>
          <option value="dark">Dark</option>
          <option value="light">Light</option>
          <option value="monokai">Monokai</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="min-lines-input" className="af-properties-tab__label">
          Minimum Lines
        </label>
        <input
          id="min-lines-input"
          type="number"
          value={minLines}
          onChange={(e) => onConfigChange('minLines', Number(e.target.value))}
          min="1"
          className="af-properties-tab__input"
          aria-label="Minimum lines"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="max-lines-input" className="af-properties-tab__label">
          Maximum Lines (0 = unlimited)
        </label>
        <input
          id="max-lines-input"
          type="number"
          value={maxLines}
          onChange={(e) => onConfigChange('maxLines', Number(e.target.value))}
          min="0"
          className="af-properties-tab__input"
          aria-label="Maximum lines"
        />
      </div>

      {validate && (
        <div className="af-properties-tab__group">
          <label htmlFor="schema-textarea" className="af-properties-tab__label">
            JSON Schema (for validation)
          </label>
          <textarea
            id="schema-textarea"
            value={JSON.stringify(schema, null, 2)}
            onChange={handleSchemaChange}
            placeholder='{"type":"object","properties":{}}'
            rows={8}
            className="af-properties-tab__textarea"
            aria-label="JSON schema"
          />
        </div>
      )}
    </div>
  );
}

export const EnhancedJsonEditorEditor: PropertyEditor = {
  type: 'enhanced-json-editor',
  label: 'Enhanced JSON Editor',
  description: 'Configure JSON editor with schema validation and formatting',
  render: renderEnhancedJsonEditorEditor,
};
